import {LabelBody, type LabelBodyProps, labelPageSize} from '@/modules/preparation-label/preparation-label-pdf'
import {Document, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import type {ComponentRow, PreparationReportData, ReportField} from './types'

/** Figma draws the report at 2× A4: 1588 px = 210 mm */
const R = 595.28 / 1588
const GREY = '#6B6B6B'
const LINE = '#DDDDDD'
const FILL = '#F3F3F3'

// column widths from the Figma table (px of 1394)
const COLUMN_WIDTHS: Record<keyof ComponentRow, number> = {
  type: 140,
  tradeName: 300,
  productCode: 140,
  substance: 210,
  batchUsage: 300,
  quantity: 160,
  memoryNumber: 146,
}

/** the label copy keeps its real size (CY-421); the frame adds its 2 px border on each side */
function labelFrameSize(size: LabelBodyProps['label']['size']) {
  const [width, height] = labelPageSize(size)
  return {width: width + 4 * R, height: height + 4 * R}
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Label',
    color: '#000',
    paddingTop: 88 * R,
    paddingBottom: 32 * R,
    paddingHorizontal: 96 * R,
    fontSize: 21 * R,
  },
  header: {flexDirection: 'row', alignItems: 'flex-start', marginBottom: 36 * R},
  logo: {width: 88 * R, height: 88 * R, objectFit: 'contain', marginRight: 32 * R},
  headerTitle: {flex: 1},
  title: {fontSize: 44 * R, fontWeight: 'bold'},
  headerSub: {fontSize: 18 * R, color: GREY, marginTop: 8 * R},
  headerRight: {alignItems: 'flex-end'},
  caption: {fontSize: 16 * R, color: GREY},
  preparationNumber: {fontSize: 36 * R, fontWeight: 'bold', marginTop: 4 * R},
  topRule: {height: 2 * R, backgroundColor: '#000', marginBottom: 36 * R},
  rule: {height: 1 * R, backgroundColor: LINE, marginVertical: 36 * R},
  sectionTitle: {fontSize: 16 * R, color: GREY, letterSpacing: 1.5 * R, marginBottom: 18 * R},
  grid: {flexDirection: 'row', flexWrap: 'wrap', columnGap: 32 * R, rowGap: 20 * R},
  cell: {width: 444 * R},
  wideCell: {width: '100%', marginTop: 20 * R},
  value: {fontSize: 21 * R, fontWeight: 'bold', marginTop: 4 * R},
  tiles: {flexDirection: 'row', gap: 20 * R, marginBottom: 12 * R},
  tile: {flex: 1, backgroundColor: FILL, borderRadius: 8 * R, paddingVertical: 18 * R, paddingHorizontal: 24 * R},
  tileValue: {fontSize: 34 * R, fontWeight: 'bold', marginTop: 6 * R},
  table: {borderWidth: 1 * R, borderColor: LINE, borderRadius: 6 * R},
  headerRow: {flexDirection: 'row', backgroundColor: FILL},
  row: {flexDirection: 'row', borderTopWidth: 1 * R, borderTopColor: LINE},
  th: {padding: 14 * R, fontSize: 15 * R, color: GREY, fontWeight: 'bold'},
  td: {padding: 14 * R, fontSize: 17 * R},
  tradeNameCell: {fontWeight: 'bold'},
  totals: {fontSize: 20 * R, marginTop: 14 * R},
  bottom: {flexDirection: 'row', gap: 40 * R},
  bottomTitle: {fontSize: 16 * R, color: GREY, letterSpacing: 1.5 * R, marginBottom: 12 * R},
  labelFrame: {borderWidth: 2 * R, borderColor: '#000'},
  signatureLine: {height: 2 * R, backgroundColor: '#000', marginTop: 48 * R},
  signatureCaption: {fontSize: 16 * R, color: GREY, marginTop: 8 * R},
})

function Fields({fields}: {fields: ReportField[]}) {
  return (
    <View style={styles.grid}>
      {fields.map(item => (
        <View key={item.caption} style={styles.cell}>
          <Text style={styles.caption}>{item.caption}</Text>
          <Text style={styles.value}>{item.value}</Text>
        </View>
      ))}
    </View>
  )
}

function ComponentsTable({report}: {report: PreparationReportData}) {
  const columns = (Object.keys(COLUMN_WIDTHS) as (keyof ComponentRow)[]).filter(
    column =>
      (column !== 'productCode' || report.componentColumns.productCode) &&
      (column !== 'memoryNumber' || report.componentColumns.memoryNumber)
  )
  const total = columns.reduce((sum, column) => sum + COLUMN_WIDTHS[column], 0)
  const width = (column: keyof ComponentRow): `${number}%` => `${(COLUMN_WIDTHS[column] / total) * 100}%`

  return (
    <View style={styles.table}>
      <View style={styles.headerRow} fixed>
        {columns.map(column => (
          <Text key={column} style={[styles.th, {width: width(column)}]}>
            {report.componentHeaders[column]}
          </Text>
        ))}
      </View>
      {report.componentRows.map(row => (
        <View key={`${row.type}-${row.tradeName}-${row.batchUsage}`} style={styles.row} wrap={false}>
          {columns.map(column => (
            <Text
              key={column}
              style={[styles.td, {width: width(column)}, column === 'tradeName' ? styles.tradeNameCell : {}]}
            >
              {row[column]}
            </Text>
          ))}
        </View>
      ))}
    </View>
  )
}

interface Props {
  report: PreparationReportData
  labelCopy: LabelBodyProps | null
  logoSrc: string | null
  pageLabel: (page: number, total: number) => string
}

export function PreparationReportPdf(props: Props) {
  return (
    <Document>
      <PreparationReportPages {...props} />
    </Document>
  )
}

/** The report pages on their own, so several reports can share one document (CY-87 reprints). */
export function PreparationReportPages({report, labelCopy, logoSrc, pageLabel}: Props) {
  return (
    <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : <View style={styles.logo} />}
          <View style={styles.headerTitle}>
            <Text style={styles.title}>{report.title}</Text>
            {report.pharmacyLine && <Text style={styles.headerSub}>{report.pharmacyLine}</Text>}
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.caption}>{report.preparationNoCaption}</Text>
            <Text style={styles.preparationNumber}>{report.preparationNumber}</Text>
            <Text
              style={styles.caption}
              render={({pageNumber, totalPages}) =>
                `${report.reportDateLine}   ·   ${pageLabel(pageNumber, totalPages)}`
              }
            />
          </View>
        </View>
        <View style={styles.topRule} fixed />

        <Text style={styles.sectionTitle}>{report.sections.patient.toUpperCase()}</Text>
        <Fields fields={report.patientFields} />
        <View style={styles.wideCell}>
          <Text style={styles.caption}>{report.administration.caption}</Text>
          <Text style={styles.value}>{report.administration.value}</Text>
        </View>
        <View style={{marginTop: 20 * R}}>
          <Fields fields={report.prescriptionFields} />
        </View>

        <View style={styles.rule} />
        <Text style={styles.sectionTitle}>{report.sections.production.toUpperCase()}</Text>
        <Fields fields={report.productionFields} />

        <View style={styles.rule} />
        <Text style={styles.sectionTitle}>{report.sections.dose.toUpperCase()}</Text>
        {report.doseRows.map(row => (
          <View key={row.map(item => item.value).join('|')} style={styles.tiles} wrap={false}>
            {row.map(item => (
              <View key={item.caption} style={styles.tile}>
                <Text style={styles.caption}>{item.caption}</Text>
                <Text style={styles.tileValue}>{item.value}</Text>
              </View>
            ))}
          </View>
        ))}

        <View style={styles.rule} />
        {/* keep the title with at least the table header and a row */}
        <Text style={styles.sectionTitle} minPresenceAhead={120 * R}>
          {report.sections.components.toUpperCase()}
        </Text>
        <ComponentsTable report={report} />
        {report.totals.map(line => (
          <Text key={line} style={styles.totals}>
            {line}
          </Text>
        ))}

        {(report.showLabelCopy || report.showSignatures) && <View style={styles.rule} />}
        {(report.showLabelCopy || report.showSignatures) && (
          // label copy and end control move to the next page together; the divider above stays behind
          <View wrap={false}>
            <View style={styles.bottom}>
              {report.showLabelCopy && labelCopy && (
                <View>
                  <Text style={styles.bottomTitle}>{report.sections.label.toUpperCase()}</Text>
                  <View style={[styles.labelFrame, labelFrameSize(labelCopy.label.size)]}>
                    <LabelBody {...labelCopy} />
                  </View>
                </View>
              )}
              {report.showSignatures && (
                <View style={{flex: 1}}>
                  <Text style={styles.bottomTitle}>{report.sections.endControl.toUpperCase()}</Text>
                  <Text style={[styles.caption, {marginTop: 22 * R}]}>{report.endControl.preparationEnd.caption}</Text>
                  <Text style={styles.value}>{report.endControl.preparationEnd.value}</Text>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureCaption}>{report.endControl.visualInspection}</Text>
                  <View style={styles.signatureLine} />
                  <Text style={styles.signatureCaption}>{report.endControl.pharmacistRelease}</Text>
                </View>
              )}
            </View>
          </View>
        )}
      </Page>
  )
}
